import { isObjectV2 } from "Utils/global.js"
import { getMyBusinessUseControlLevelLink } from "Utils/checkAccess.js"
import {
  getLevelSelectOptions,
  getTypeControlSelectOptions,
} from "Containers/Governance/ControlParameter/utils.js"
import { achievementPeriodIsLongerThanPeriod } from "Components/Governance/utils.js"

export const periodAcceptAchievementPeriod = ["Trimestrielle", "Semestrielle", "Annuelle"]

export const mandatoryFieldControlParameters = [
  "color",
  "endDate",
  "startDate",
  "level",
  "objectifControl",
  "period",
  "process",
  "reference",
  "type_control",
  "sample",
  "riskIds",
]

export const IS_CREATION_NEW_CONTROL = "isCreationNewControl"
export const IS_EDIT_SAVE_CONTROL = "isEditSaveControl"
export const IS_EDIT_PUBLISHED_CONTROL = "isEditPublishedControl"
export const IS_READ_ONLY_CONTROL = "isReadOnlyControl"

export const getDefaultGlobalControl = trad => {
  const controlLevelOptions = getLevelSelectOptions()
  const controlTypeOptions = getTypeControlSelectOptions(trad)
  return {
    control: {
      reference: "",
      process: "",
      level: controlLevelOptions[0]?.value,
      type_control: controlTypeOptions[0]?.value,
      mitigantType: "",
      riskIds: [],
      controller: "",
      period: "",
      startDate: new Date().toLocaleDateString("fr"),
      endDate: new Date().toLocaleDateString("fr"),
      isBusinessDaysDate: false,
      sample: "",
      comments_sample: "",
      description: "",
      comments: "",
      objectifControl: "",
      color: "#9AAE31",
      filesAttachedIds: [],
      requestedDocuments: [],
      regulatoryReference: "",
      parentControlsIds: [],
      thematicId: "",
      targetUsers: {
        businessId: "",
        usersIds: [],
        userGroup: [],
      },
    },
    template: {
      businessId: "",
      date_created: "",
      date_update: "",
      id_user_created: "",
      id_user_update: "",
      name_template: "",
      name_version: "1.0.0",
      parameters: {},
      statut: "",
      type_template: "",
      user_created: "",
      user_update: "",
      structure_template: [],
      targetUsers: {
        businessId: "",
        usersIds: [],
        userGroup: [],
      },
    },
  }
}

export const getPeriodSelectOptionDatesCalendar = trad => {
  const { DAILY, WEEKLY, MONTHLY, ANNUAL, QUARTERLY, SEMI_ANNUAL, ANY } = trad

  const selectPeriodOptions = [
    { value: "Quotidienne", label: DAILY },
    { value: "Hebdomadaire", label: WEEKLY },
    { value: "Mensuelle", label: MONTHLY },
    { value: "Trimestrielle", label: QUARTERLY },
    { value: "Semestrielle", label: SEMI_ANNUAL },
    { value: "Annuelle", label: ANNUAL },
    { value: "Aucune", label: ANY },
  ]
  return selectPeriodOptions
}

export const getPeriodSelectOptionBusinessDays = trad => {
  const { MONTHLY, QUARTERLY, SEMI_ANNUAL, ANNUAL, ANY } = trad

  const selectPeriodOptions = [
    { value: "Mensuelle", label: MONTHLY },
    { value: "Trimestrielle", label: QUARTERLY },
    { value: "Semestrielle", label: SEMI_ANNUAL },
    { value: "Annuelle", label: ANNUAL },
    { value: "Aucune", label: ANY },
  ]
  return selectPeriodOptions
}

export const getDateEffectSelectOptions = trad => {
  const {
    FIRST_DAY_WORKING,
    FIVE_DAY_WORKING,
    TEN_DAY_WORKING,
    FIFTEEN_DAY_WORKING,
    TWENTY_DAY_WORKING,
  } = trad

  const selectDateOptions = [
    { value: 1, label: FIRST_DAY_WORKING },
    { value: 5, label: FIVE_DAY_WORKING },
    { value: 10, label: TEN_DAY_WORKING },
    { value: 15, label: FIFTEEN_DAY_WORKING },
    { value: 20, label: TWENTY_DAY_WORKING },
  ]
  return selectDateOptions
}

export const getRisksByActivitiesLinkedToControl = (allActivities, allRisks, control) => {
  const risksIds = control?.riskIds
  if (!Array.isArray(allActivities) || !Array.isArray(allRisks) || !Array.isArray(risksIds))
    return []

  const risksAssociatedToControl = allRisks.filter(risk => risksIds.includes(risk?._id?.$oid))
  const idsActivities = risksAssociatedToControl.map(risk => risk?.activity)
  const activities = allActivities.filter(activity => idsActivities.includes(activity?._id?.$oid))

  const embeddedActivities = activities.map(activity => {
    const risksByActivity = risksAssociatedToControl.filter(
      risk => risk?.activity === activity?._id?.$oid
    )
    return {
      ...activity,
      risks: risksByActivity,
    }
  })
  return embeddedActivities
}

export const getStatusEditionControlWithTemplate = template => {
  if (!isObjectV2(template)) return ""
  const isEditSaveControl = template?.statut === "draft"
  const isEditPublishedControl = template?.statut === "published"

  if (isEditSaveControl) return IS_EDIT_SAVE_CONTROL
  if (isEditPublishedControl) return IS_EDIT_PUBLISHED_CONTROL
  return ""
}

const parameterAcceptedToPublishControl = [
  "_id",
  "color",
  "reference",
  "process",
  "startDate",
  "endDate",
  "controller",
  "period",
  "achievementPeriod",
  "sample",
  "comments_sample",
  "isBusinessDaysDate",
  "description",
  "comments",
  "objectifControl",
  "filesAttachedIds",
  "requestedDocuments",
  "regulatoryReferences",
  "targetUsers",
  "level",
  "type_control",
  "mitigantType",
  "riskIds",
  "executionTime",
  "thematicId",
  "parentControlsIds",
  "regulatoryReference",
  "active",
  "type_of_control",
  "agence",
]

export const getParametersAcceptedToPublishedControl = controlParameters => {
  let newControlParameters = {}
  if (!isObjectV2(controlParameters)) return newControlParameters
  const keysParameters = Object.keys(controlParameters)
  keysParameters.forEach(key => {
    if (parameterAcceptedToPublishControl.includes(key)) {
      newControlParameters[key] = controlParameters[key]
    }
  })
  return newControlParameters
}

export const getControlThematicValue = (control, thematicValues) => {
  if (!control || !Array.isArray(thematicValues)) return null
  const { thematicLabel, thematicId } = control
  const selectedThematic = thematicValues.find(theme => theme.uuid === thematicId)
  if (selectedThematic) return selectedThematic
  else if (thematicLabel) return { value: "", label: thematicLabel }
  else return null
}

export const getThematicOptions = thematicList => {
  const values = thematicList?.values
  if (!Array.isArray(values)) return []
  return values.filter(val => !val.disabled)
}

export const getAchievementPeriodLabel = achievementPeriodValue => {
  let mappingValue_Label = {}

  for (let i = 1; i <= 12; i++) {
    mappingValue_Label[`between0And${i}Month`] = `Entre 0 et ${i} mois`
  }
  return mappingValue_Label[achievementPeriodValue]
}

export const getAchievementPeriodOptions = (control, endOffsetPeriod) => {
  let values = []

  if (control?.period === "Trimestrielle") {
    values = ["between0And1Month", "between0And2Month", "between0And3Month"]
  } else if (control?.period === "Semestrielle") {
    values = [
      "between0And1Month",
      "between0And2Month",
      "between0And3Month",
      "between0And4Month",
      "between0And5Month",
      "between0And6Month",
    ]
  } else if (control?.period === "Annuelle") {
    values = [
      "between0And1Month",
      "between0And2Month",
      "between0And3Month",
      "between0And4Month",
      "between0And5Month",
      "between0And6Month",
      "between0And7Month",
      "between0And8Month",
      "between0And9Month",
      "between0And10Month",
      "between0And11Month",
      "between0And12Month",
    ]

    // if (control?._id) {
    //   values = getFilteredValues(defaultValues, endOffsetSelected)
    // } else {
    //   values = defaultValues
    // }
  }

  return values.map(val => ({ value: val, label: getAchievementPeriodLabel(val) }))
}

// function getFilteredValues(values, endOffset) {
//   return values.filter(value => {
//     const endIndex = values.indexOf(endOffset)
//     const currentValueIndex = values.indexOf(value)

//     return currentValueIndex >= endIndex
//   })
// }

//TODO: need refacto
export const getAchievementPeriodFromSelectedValue = selected => {
  if (selected === "between0And1Month") return { startOffset: "P0M", endOffset: "P1M" }
  if (selected === "between0And2Month") return { startOffset: "P0M", endOffset: "P2M" }
  if (selected === "between0And3Month") return { startOffset: "P0M", endOffset: "P3M" }
  if (selected === "between0And4Month") return { startOffset: "P0M", endOffset: "P4M" }
  if (selected === "between0And5Month") return { startOffset: "P0M", endOffset: "P5M" }
  if (selected === "between0And6Month") return { startOffset: "P0M", endOffset: "P6M" }
  if (selected === "between0And7Month") return { startOffset: "P0M", endOffset: "P7M" }
  if (selected === "between0And8Month") return { startOffset: "P0M", endOffset: "P8M" }
  if (selected === "between0And9Month") return { startOffset: "P0M", endOffset: "P9M" }
  if (selected === "between0And10Month") return { startOffset: "P0M", endOffset: "P10M" }
  if (selected === "between0And11Month") return { startOffset: "P0M", endOffset: "P11M" }
  if (selected === "between0And12Month") return { startOffset: "P0M", endOffset: "P12M" }

  return { startOffset: "P0M", endOffset: "P1M" }
}

export const getSelectedValueFromAchievementPeriod = endOffset => {
  if (endOffset === "P1M") return "between0And1Month"
  if (endOffset === "P2M") return "between0And2Month"
  if (endOffset === "P3M") return "between0And3Month"
  if (endOffset === "P4M") return "between0And4Month"
  if (endOffset === "P5M") return "between0And5Month"
  if (endOffset === "P6M") return "between0And6Month"
  if (endOffset === "P7M") return "between0And7Month"
  if (endOffset === "P8M") return "between0And8Month"
  if (endOffset === "P9M") return "between0And9Month"
  if (endOffset === "P10M") return "between0And10Month"
  if (endOffset === "P11M") return "between0And11Month"
  if (endOffset === "P12M") return "between0And12Month"
}

export const getControlAchievementPeriodSelected = (
  achievementPeriodOptions,
  achievementPeriod
) => {
  if (Array.isArray(achievementPeriodOptions) && !achievementPeriod) return null

  let value = null
  const { startOffset, endOffset } = achievementPeriod
  if (endOffset && startOffset === "P0M") {
    //TODO: need refacto
    if (endOffset === "P1M") value = "between0And1Month"
    if (endOffset === "P2M") value = "between0And2Month"
    if (endOffset === "P3M") value = "between0And3Month"
    if (endOffset === "P4M") value = "between0And4Month"
    if (endOffset === "P5M") value = "between0And5Month"
    if (endOffset === "P6M") value = "between0And6Month"
    if (endOffset === "P7M") value = "between0And7Month"
    if (endOffset === "P8M") value = "between0And8Month"
    if (endOffset === "P9M") value = "between0And9Month"
    if (endOffset === "P10M") value = "between0And10Month"
    if (endOffset === "P11M") value = "between0And11Month"
    if (endOffset === "P12M") value = "between0And12Month"
  }

  let optionFound = achievementPeriodOptions.find(achievementP => achievementP.value === value)
  return optionFound ? optionFound : null
}

export const shouldDisplayAchievementPeriodSelector = (myBusiness, controlPeriod) => {
  const useAchivementPeriod = myBusiness?.paramsControls?.useAchievementPeriod
  const isCorrectFrequency = periodAcceptAchievementPeriod.includes(controlPeriod)
  const displayAchivementPeriod = Boolean(useAchivementPeriod && isCorrectFrequency)
  return displayAchivementPeriod
}

export const getFullControlAfterPeriodChange = (previousFullControl, newPeriod) => {
  const previousAchievementPeriod = previousFullControl?.control?.achievementPeriod
  const achievementPeriodTooLong = achievementPeriodIsLongerThanPeriod(
    previousAchievementPeriod,
    newPeriod
  )
  const newPeriodDontAcceptAchievement = !periodAcceptAchievementPeriod.includes(newPeriod)
  const needToResetAchievementperiod = achievementPeriodTooLong || newPeriodDontAcceptAchievement
  return {
    ...previousFullControl,
    control: {
      ...previousFullControl.control,
      period: newPeriod,
      ...(needToResetAchievementperiod && { achievementPeriod: null }), // We set null instead of undefined because back-end dont catch key with undefined value
    },
  }
}

export const getIfControlParametersAreValid = (control, mandatoryParameters, myBusiness) => {
  if (!control || !Array.isArray(mandatoryParameters) || !myBusiness) return false
  const businessUseControlsAssociate = getMyBusinessUseControlLevelLink(myBusiness)
  const parametersToVerify = [...mandatoryParameters]
  if (businessUseControlsAssociate && control?.level === "2") {
    parametersToVerify.push("parentControlsIds")
  }
  const someParamsAreMissing = parametersToVerify.some(param => !control.hasOwnProperty(param))
  if (someParamsAreMissing) return false
  const someParamsAreEmpty = parametersToVerify.some(param => !control[param]?.length)
  if (someParamsAreEmpty) return false

  const controlNeedAchievementPeriod = shouldDisplayAchievementPeriodSelector(
    myBusiness,
    control?.period
  )
  if (controlNeedAchievementPeriod) {
    return Boolean(control?.achievementPeriod)
  }

  return true
}

export const getIfUserCanAddDocuments = statusEditionControl => {
  if (!statusEditionControl) return false
  return [IS_CREATION_NEW_CONTROL, IS_EDIT_SAVE_CONTROL].includes(statusEditionControl)
}

export const getIfUserCanEditDocuments = statusEditionControl => {
  if (!statusEditionControl) return false
  return [IS_CREATION_NEW_CONTROL, IS_EDIT_SAVE_CONTROL, IS_EDIT_PUBLISHED_CONTROL].includes(
    statusEditionControl
  )
}

export const getIfUserCanDeleteDocuments = statusEditionControl => {
  if (!statusEditionControl) return false
  return [IS_CREATION_NEW_CONTROL, IS_EDIT_SAVE_CONTROL].includes(statusEditionControl)
}

export const getIfUserCanAddControlPoints = statusEditionControl => {
  if (!statusEditionControl) return false
  return [IS_CREATION_NEW_CONTROL, IS_EDIT_SAVE_CONTROL, IS_EDIT_PUBLISHED_CONTROL].includes(
    statusEditionControl
  )
}

export const getIfUserCanDeleteControlPoints = statusEditionControl => {
  if (!statusEditionControl) return false
  return [IS_CREATION_NEW_CONTROL, IS_EDIT_SAVE_CONTROL, IS_EDIT_PUBLISHED_CONTROL].includes(
    statusEditionControl
  )
}

export const getIfUserCanEditControlPoints = statusEditionControl => {
  if (!statusEditionControl) return false
  return [IS_CREATION_NEW_CONTROL, IS_EDIT_SAVE_CONTROL].includes(statusEditionControl)
}

export const getListAgenceCSF = () => {
  return [
    { value: "BEAUVAIS", label: "BEAUVAIS" },
    { value: "BOBIGNY", label: "BOBIGNY" },
    { value: "BORDEAUX", label: "BORDEAUX" },
    { value: "CERGY", label: "CERGY" },
    { value: "CRETEIL", label: "CRETEIL" },
    { value: "DIJON", label: "DIJON" },
    { value: "EVRY", label: "EVRY" },
    { value: "GRENOBLE", label: "GRENOBLE" },
    { value: "GUADELOUPE", label: "GUADELOUPE" },
    { value: "LA REUNION", label: "LA REUNION" },
    { value: "LENS", label: "LENS" },
    { value: "LILLE", label: "LILLE" },
    { value: "LYON", label: "LYON" },
    { value: "MARSEILLE", label: "MARSEILLE" },
    { value: "MARTINIQUE", label: "MARTINIQUE" },
    { value: "MELUN", label: "MELUN" },
    { value: "MONTPELLIER", label: "MONTPELLIER" },
    { value: "NANCY", label: "NANCY" },
    { value: "NANTERRE", label: "NANTERRE" },
    { value: "NANTES", label: "NANTES" },
    { value: "NICE", label: "NICE" },
    { value: "PARIS", label: "PARIS" },
    { value: "PAU", label: "PAU" },
    { value: "REIMS", label: "REIMS" },
    { value: "RENNES", label: "RENNES" },
    { value: "ROUEN", label: "ROUEN" },
    { value: "STRASBOURG", label: "STRASBOURG" },
    { value: "TELEVENTE", label: "TELEVENTE" },
    { value: "TOULOUSE", label: "TOULOUSE" },
    { value: "TOURS", label: "TOURS" },
    { value: "VERSAILLES", label: "VERSAILLES" },
  ]
}
